import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import type { CheckoutPassSelectionDto } from '@/services/orders.service';
import type { CalculateOffersPerson, Domain, OfferItem } from '@/services/inventory-api.service';
import { inventoryApi } from '@/services/inventory-api.service';
import { normalizeCalculateOffersResponse } from '@/utils/calculate-offers';
import {
  buildAgeMapFromRestrictions,
  buildBirthDateMapFromRestrictions,
  buildPeopleFromSkierCounts,
} from '@/utils/calculate-offers-people';
import { isValidBirthDate } from '@/utils/booking';
import {
  collectGlobalAgeCategoryLabels,
  domainOffersHasCategoryLabel,
  mergeOfferItemsForDisplay,
} from '@/utils/merge-offer-display-items';
import { offerCategoryMatchesOverride } from '@/utils/offer-category-match';
import { expandPassSelections } from '@/utils/skier-slots';
import type { BookingSkierFormData, SkierCounts } from '@/store/book-ski-pass.store';
import { useEffect, useMemo, useState } from 'react';

function getAgeForCategory(ageMap: Record<string, number>, ageCategory: string): number {
  const key = Object.keys(ageMap).find((k) => k.toLowerCase() === ageCategory.toLowerCase());
  return key ? ageMap[key] ?? 30 : 30;
}

function pickMatchingOfferItem(items: OfferItem[], categoryLabel: string): OfferItem | null {
  return (
    items.find((item) => item.type === 'pass' && offerCategoryMatchesOverride(item.customer_type, categoryLabel)) ?? null
  );
}

function buildSkierCountsForSingleCategory(skierCounts: SkierCounts, categoryLabel: string): SkierCounts {
  for (const [k, v] of Object.entries(skierCounts)) {
    if (v > 0 && offerCategoryMatchesOverride(k, categoryLabel)) {
      return { [k]: 1 };
    }
  }
  return {};
}

function buildPeopleForCategoryProbe(
  categoryLabel: string,
  passSelections: CheckoutPassSelectionDto[],
  skierForms: BookingSkierFormData[],
  startDate: string,
  days: number,
  ageMap: Record<string, number>,
  birthDateMap: Record<string, string>,
  skierCounts: SkierCounts
): CalculateOffersPerson[] {
  const slots = expandPassSelections(passSelections);
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot) continue;
    if (offerCategoryMatchesOverride(slot.ageCategory, categoryLabel)) {
      const bd = skierForms[i]?.birthDate?.trim();
      if (bd && isValidBirthDate(bd)) {
        return [{ startDate, days, birthDate: bd }];
      }
    }
  }
  for (const f of skierForms) {
    const bd = f.birthDate.trim();
    if (bd && isValidBirthDate(bd)) {
      return [{ startDate, days, birthDate: bd }];
    }
  }
  const partial = buildSkierCountsForSingleCategory(skierCounts, categoryLabel);
  if (Object.keys(partial).length > 0) {
    return buildPeopleFromSkierCounts(partial, startDate, days, ageMap, birthDateMap);
  }
  return [{ startDate, days, age: getAgeForCategory(ageMap, categoryLabel) }];
}

export type UseSupplementalOfferLinesOptions = {
  mergedByDomain: DomainOffersEntry[];
  passSelections: CheckoutPassSelectionDto[];
  skierForms: BookingSkierFormData[];
  stepTwoStartingDate: string;
  stepTwoDaysOfSkiing: string;
  stepTwoSkierCounts: SkierCounts;
  selectedDomain: Domain | null;
};

export function useSupplementalOfferLinesAcrossDomains(
  options: UseSupplementalOfferLinesOptions
): DomainOffersEntry[] {
  const {
    mergedByDomain,
    passSelections,
    skierForms,
    stepTwoStartingDate,
    stepTwoDaysOfSkiing,
    stepTwoSkierCounts,
    selectedDomain,
  } = options;

  const [supplementalByPk, setSupplementalByPk] = useState<Record<number, OfferItem[]>>({});

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setSupplementalByPk({});
      if (mergedByDomain.length === 0) return;

      const days = parseInt(stepTwoDaysOfSkiing, 10);
      const startDate = stepTwoStartingDate;
      if (!startDate || Number.isNaN(days) || days < 1) return;

      const ageMap = buildAgeMapFromRestrictions(selectedDomain?.restrictions);
      const birthDateMap = buildBirthDateMapFromRestrictions(
        selectedDomain?.restrictions,
        startDate
      );

      const globalLabels = collectGlobalAgeCategoryLabels(mergedByDomain, passSelections);
      if (globalLabels.length === 0) return;

      const probes: Array<{ pk: number; label: string; people: CalculateOffersPerson[] }> = [];

      for (const entry of mergedByDomain) {
        for (const label of globalLabels) {
          if (domainOffersHasCategoryLabel(entry, label)) continue;

          const people = buildPeopleForCategoryProbe(
            label,
            passSelections,
            skierForms,
            startDate,
            days,
            ageMap,
            birthDateMap,
            stepTwoSkierCounts
          );
          if (people.length === 0) continue;
          probes.push({ pk: entry.domain.pk, label, people });
        }
      }

      if (probes.length === 0) return;

      const probeOffsets: Array<{ start: number; end: number }> = [];
      let cursor = 0;
      for (const probe of probes) {
        const start = cursor;
        cursor += probe.people.length;
        probeOffsets.push({ start, end: cursor });
      }

      const expandedPeople = probes.flatMap((probe) =>
        probe.people.map((person) => ({
          ...person,
          domain_id: probe.pk,
        }))
      );

      const raw = await inventoryApi
        .calculateOffersBatch({
          people: expandedPeople,
          options: { partial_failures_allowed: true },
        })
        .catch((): null => null);

      if (!raw || cancelled) return;

      const responsePeople = Array.isArray(raw.people) ? raw.people : [];
      const responseItems = Array.isArray(raw.items) ? raw.items : [];

      const results: Array<{ pk: number; item: OfferItem } | null> = probes.map((probe, index) => {
        const offset = probeOffsets[index];
        if (!offset) return null;

        const peopleSlice = responsePeople.slice(offset.start, offset.end);
        const peopleItems = peopleSlice.flatMap((person) => person.items ?? []);

        const itemsByDomain = responseItems.filter((item) => item.domain_id === probe.pk);
        const probeItems = peopleItems.length > 0 ? peopleItems : itemsByDomain;

        const norm = normalizeCalculateOffersResponse({
          ...raw,
          people: peopleSlice,
          items: probeItems,
        });
        const item = pickMatchingOfferItem(norm.items, probe.label);
        if (!item) return null;
        return { pk: probe.pk, item };
      });

      if (cancelled) return;

      const byPk: Record<number, OfferItem[]> = {};
      for (const r of results) {
        if (!r) continue;
        const list = byPk[r.pk] ?? [];
        list.push(r.item);
        byPk[r.pk] = list;
      }

      setSupplementalByPk(byPk);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    mergedByDomain,
    passSelections,
    skierForms,
    stepTwoStartingDate,
    stepTwoDaysOfSkiing,
    stepTwoSkierCounts,
    selectedDomain,
  ]);

  return useMemo(() => {
    return mergedByDomain.map((entry) => {
      const extra = supplementalByPk[entry.domain.pk];
      if (!extra?.length) return entry;
      return {
        ...entry,
        offers: {
          ...entry.offers,
          items: mergeOfferItemsForDisplay(entry.offers.items, extra),
        },
      };
    });
  }, [mergedByDomain, supplementalByPk]);
}
